import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { InfoContent } from './InfoContent';

import { Button, Modal } from '@/components';
import {
  MainBox,
  MainHome,
  TourneyList,
} from '@/components/GenericsTournaments';
import { IconAtention } from '@/components/IconAtention';
import ModalTkred from '@/components/ModalTkred';
import useAuth from '@/hooks/useAuth';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import { useIsLastMatchDay } from '@/hooks/useIsLastMatchDate';
import useToast from '@/hooks/useToast';
import useTourney from '@/hooks/useTourney';
import ROUTES from '@/router/routes';
import { ActionTypeManageFCupEnum } from '@/services/friendsCup';
import {
  acceptMlInvitation,
  basicDataMinileague,
  MiniLeagueErrorCode,
  requestPostulantManagementMl,
  requestPostulationMl,
} from '@/services/minileagues';
import { Maybe } from '@/types';
import { BasicDataGDA } from '@/types/friendCup';
import { InvitationType } from '@/types/invites';
import { MiniLigasTO, ResponseInitMiniligas } from '@/types/minileagues';
import { ActionsTourneys, GenericItemTourneysData } from '@/types/tda';
import { EnumContextGDT } from '@/types/team';
import {
  cleanStorageRedirectInvitation,
  cn,
  participantsMl,
  STATUS_ID_INVITATION,
  STATUS_TOURNEY_INVITATION,
  TEXTS,
} from '@/utils';

interface Props {
  playingList: MiniLigasTO[] | null;
  previousList: MiniLigasTO[] | null;
  pendingList: MiniLigasTO[] | null;
  refetchFather: (refetchAll?: boolean) => void;
  isMounted: boolean;
  initData: Maybe<ResponseInitMiniligas>;
  invitationList: MiniLigasTO[] | null;
  isParticipationWaitingForAutomaticMl: boolean;
  isMiniLeaguesInProgress: boolean;
}

type TypesState = {
  showModalInfo: boolean;
  nameUser: string;
  showModalRequest: boolean;
  idMiniLiga: number | undefined;
  // Nuevos campos para manejar invitaciones desde URL
  showModalInvitationFromUrl: boolean;
  invitationDetails: BasicDataGDA | null;
  processingInvitation: boolean;
};

const initialState = {
  showModalInfo: false,
  nameUser: '',
  showModalRequest: false,
  idMiniLiga: undefined,
  // Inicializar nuevos campos
  showModalInvitationFromUrl: false,
  invitationDetails: null,
  processingInvitation: false,
};

export const MainContent = React.memo(
  ({
    invitationList,
    isMounted,
    previousList,
    refetchFather,
    playingList,
    initData,
    pendingList,
    isParticipationWaitingForAutomaticMl,
    isMiniLeaguesInProgress,
  }: Props) => {
    const { isDesktop, isMobile } = useIsDesktop();
    const [state, setState] = useState<TypesState>(initialState);
    const [isAlreadyInvited, setIsAlreadyInvited] = useState(false);
    const navigate = useNavigate();
    const { userInitialData: userLogged } = useAuth();
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const toast = useToast();
    const fetchedRef = useRef(false);
    const { tourneyData, updateTourney } = useTourney();
    const dataPostLoginTkred =
      sessionStorage.getItem('postLoginTkred') || undefined;
    const location = useLocation();
    const isLastMatchDay = useIsLastMatchDay();

    const MoreInfoButton = useCallback(
      ({ className }: { className?: string }) => (
        <Button
          className={className}
          type="button"
          variant="text"
          onClick={() => {
            updateTourney({ showModalInfo: true });
          }}
        >
          Más información y Premios
        </Button>
      ),
      [updateTourney]
    );

    // Verificar si hay una invitación pendiente al cargar el componente(externalizar)
    useEffect(() => {
      const checkPendingInvitation = () => {
        const storedInvitationId = localStorage.getItem('invitationId');
        const storedInvitationType = localStorage.getItem('invitationType');
        const storedInvitationUrl = localStorage.getItem('invitationUrl');

        if (
          storedInvitationId &&
          storedInvitationType === InvitationType.MINILIGA &&
          storedInvitationUrl
        ) {
          const invitationId = Number(storedInvitationId);

          // Verificar si ya estamos participando en este torneo
          const alreadyPlaying = playingList?.some(
            (item) => item.id === invitationId
          );

          // Verificar si ya tenemos una invitación pendiente para este torneo
          // Verificamos en ambas listas de pendientes
          const alreadyInvited = invitationList?.some(
            (item) => item.id === invitationId
          );
          setIsAlreadyInvited(alreadyInvited || false);

          // Verificar si estamos postulados a ese torneo
          const alreadyPostulated = pendingList?.some(
            (item) => item.id === invitationId
          );

          if (alreadyPlaying || alreadyPostulated) {
            // Asegurarnos de que el modal no se muestre
            setState(initialState);
            // Si ya estamos participando o postulados, limpiar el localStorage
            if (
              localStorage.getItem('invitationType') === InvitationType.MINILIGA
            ) {
              cleanStorageRedirectInvitation();
            }

            // Mostrar un mensaje informativo
            if (alreadyPlaying) {
              toast.show('Ya estás participando en la  miniliga');
            } else if (alreadyPostulated) {
              toast.show(
                'Ya estás postulado a la miniliga, actualmente estás a la espera de que te acepte el dueño.'
              );
            }
          } else {
            setState((prevState) => ({
              ...prevState,
              showModalInvitationFromUrl:
                isMounted && !alreadyPlaying && !alreadyPostulated,
              idMiniLiga: Number(invitationId),
            }));
          }
        }
      };

      checkPendingInvitation();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pendingList, playingList, invitationList]);

    const cleanAfterInvitationUrl = () => {
      cleanStorageRedirectInvitation();
      setState(initialState);
    };

    // Obtener detalles de la invitación cuando se muestra el modal(externalizar)
    useEffect(() => {
      const fetchInvitationDetails = async () => {
        const txtError = 'El id de la miniliga es inválido';
        if (
          state.showModalInvitationFromUrl &&
          state.idMiniLiga &&
          !fetchedRef.current
        ) {
          fetchedRef.current = true;
          try {
            const response = await basicDataMinileague(state.idMiniLiga);

            if (!response.estado) {
              console.error(txtError);
              toast.show(txtError);
              cleanAfterInvitationUrl();
              return;
            }

            if (response && response.estado) {
              setState((prevState: any) => ({
                ...prevState,
                invitationDetails: response,
                nameUser:
                  response.nombreDuenio
                    ?.concat(' ')
                    .concat(
                      response.apellidoDuenio ? response.apellidoDuenio : ''
                    ) ?? 'Alguien',
              }));
            }
          } catch (error) {
            toast.show(txtError);
            cleanAfterInvitationUrl();
            throw new Error(txtError);
          }
        }
      };

      fetchInvitationDetails();
    }, [state.showModalInvitationFromUrl, state.idMiniLiga, toast]);

    const handleButtonCreate = () => {
      if (userLogged?.dtExtranjero) {
        return toast.show(TEXTS.exclusiveForArgentinianDTs);
      }
      navigate(ROUTES.minileagues.create);
    };

    const txtCenteredDesktop = useCallback((item: MiniLigasTO) => {
      const isFull = item.idEstadoML === 2;
      const isEmpty = !item?.cantidadParticipantes;

      if (isEmpty) return null;

      return (
        <span
          className={cn(
            'text-2xs',
            !isFull ? 'text-gdt-status-red' : 'text-gdt-status-green'
          )}
        >
          {!isFull ? (
            <div className="flex items-center">
              <IconAtention className="mr-2" color={'gdt-status.red.DEFAULT'} />{' '}
              <span>
                {participantsMl(
                  item?.cantidadParticipantes || 0,
                  tourneyData.maxNumParticipants
                )}
              </span>
            </div>
          ) : (
            'Completa'
          )}
        </span>
      );
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const invitationDataList = useMemo((): GenericItemTourneysData[] | null => {
      if (!invitationList) return null;
      return invitationList.map((item: MiniLigasTO) => {
        return {
          id: item?.id,
          title: item?.nombreML,
          subtitle: item?.nombreAdm,
          disabled: item?.idEstadoML === 2,
          secundaryText: isDesktop
            ? item?.nombreAdm
            : participantsMl(
                item?.cantidadParticipantes || 0,
                tourneyData.maxNumParticipants
              ),
          textCentered: isDesktop ? txtCenteredDesktop(item) : undefined,
        };
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isDesktop, invitationList, txtCenteredDesktop]);

    const previousDataList = useMemo((): GenericItemTourneysData[] | null => {
      if (!previousList) return null;
      return previousList.map((item: MiniLigasTO) => ({
        id: item.id,
        title: item.nombreML,
        textCentered: isDesktop ? txtCenteredDesktop(item) : undefined,
      }));
    }, [isDesktop, previousList, txtCenteredDesktop]);

    const disablingAccess = useCallback(
      (item: MiniLigasTO): boolean => {
        if (item.idEstadoML === 2) {
          return false;
        } else if (item.idUsuarioOwner === userLogged?.idUsuarioDT) {
          return false;
        }
        return true;
      },
      [userLogged?.idUsuarioDT]
    );

    const playingDataList = useMemo((): GenericItemTourneysData[] | null => {
      if (!playingList) return null;

      return playingList.map((item: MiniLigasTO) => {
        return {
          id: item?.id,
          title: item?.nombreML,
          disabled: disablingAccess(item),
          secundaryText: isMobile
            ? participantsMl(
                item?.cantidadParticipantes || 0,
                tourneyData.maxNumParticipants
              ).toUpperCase()
            : undefined,
          textCentered: isDesktop ? txtCenteredDesktop(item) : undefined,
        };
      });
    }, [
      disablingAccess,
      isDesktop,
      isMobile,
      playingList,
      tourneyData.maxNumParticipants,
      txtCenteredDesktop,
    ]);

    const pendingDataList = useMemo((): GenericItemTourneysData[] | null => {
      if (!pendingList) return null;

      return pendingList.map((item: MiniLigasTO) => ({
        id: item.id,
        title: item.nombreML,
        status:
          item.idEstadoParticipacion === STATUS_ID_INVITATION.RECHAZADO
            ? STATUS_TOURNEY_INVITATION.REJECTED
            : STATUS_TOURNEY_INVITATION.PENDING,
        secundaryText: isMobile
          ? participantsMl(
              item?.cantidadParticipantes || 0,
              tourneyData.maxNumParticipants
            ).toUpperCase()
          : undefined,
        textCentered: isDesktop ? txtCenteredDesktop(item) : undefined,
      }));
    }, [
      isDesktop,
      isMobile,
      pendingList,
      tourneyData.maxNumParticipants,
      txtCenteredDesktop,
    ]);

    //  TRATAR ACEPTAR/RECHAZAR INVITACIONES RECIBIDAS
    const onHandleRequestActions = useCallback(
      async (actionType: ActionTypeManageFCupEnum, idMiniLiga?: number) => {
        if (state.processingInvitation) {
          return;
        }

        setState((prevState) => ({
          ...prevState,
          processingInvitation: true,
        }));

        try {
          // Usar la nueva función acceptMlInvitation para aceptar invitaciones
          if (
            actionType === ActionTypeManageFCupEnum.ACC_ACEPTAR_INVITACION &&
            idMiniLiga
          ) {
            const response = await acceptMlInvitation(idMiniLiga);

            if (
              !response.estado &&
              response.codError ===
                MiniLeagueErrorCode.MINI_LIGAS_FINALIZADO_ACEPT
            ) {
              toast.show('Finalizó la registración.');

              refetchFather(true);

              return;
            }

            if (response.estado && isAlreadyInvited) {
              toast.show(
                'Tenías una invitación pendiente para la miniliga ' +
                  state.invitationDetails?.nombre +
                  '. Ya estás participando.'
              );
            } else if (response.estado) {
              toast.show('Gracias por aceptar la invitación.');
            } else {
              // Mostrar el mensaje de error específico devuelto por la API
              console.error(
                'Error al aceptar la invitación:',
                response.mensaje
              );
              toast.show(response.mensaje || TEXTS.defaultError);
            }
          } else {
            // Para otras acciones, seguir usando la función existente
            const responseManagePostulant = await requestPostulantManagementMl({
              tipoAccion: actionType,
              idMiniLiga: idMiniLiga || 0,
            });

            if (responseManagePostulant.estado) {
              if (
                actionType === ActionTypeManageFCupEnum.ACC_RECHAZAR_INVITACION
              ) {
                // toast.show('Has rechazado la invitación');
              }
            } else {
              // Mostrar el mensaje de error específico devuelto por la API
              console.error('Error:', responseManagePostulant.mensaje);
              toast.show(responseManagePostulant.mensaje || TEXTS.defaultError);
            }
          }

          refetchFather();
        } catch (error) {
          console.error(error);
          toast.show(TEXTS.defaultError);
        } finally {
          // Limpiar localStorage si la invitación vino de una URL
          if (localStorage.getItem('invitationType') === 'miniliga') {
            cleanStorageRedirectInvitation();
          }

          setState(initialState);
          fetchedRef.current = false;
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [refetchFather, toast, state.processingInvitation, initData?.comenzaron]
    );

    // VER MODAL SOLICITUD RECIBIDA
    const onHandleRequestRecived = useCallback(
      async (params: ActionsTourneys) => {
        const item = invitationDataList?.find((user) => user.id === params.id);

        if (!item?.id) return;

        if (isMobile) {
          setState((prevState: TypesState) => {
            return {
              ...prevState,
              showModalRequest: true,
              nameUser: String(item?.subtitle),
              idMiniLiga: item.id,
            };
          });
        } else {
          if (params.action === 'reject') {
            onHandleRequestActions(
              ActionTypeManageFCupEnum.ACC_RECHAZAR_INVITACION,
              item.id
            );
          } else {
            onHandleRequestActions(
              ActionTypeManageFCupEnum.ACC_ACEPTAR_INVITACION,
              item.id
            );
          }
        }
      },
      [isMobile, onHandleRequestActions, invitationDataList]
    );

    const RequestRecivedListUI = useCallback(() => {
      return (
        invitationDataList &&
        invitationDataList?.length > 0 && (
          <TourneyList
            action={{
              txtAction: 'Ver solicitud',
              variantButton: 'secondary',
              txtActionDisabled: 'Ver solicitud',
            }}
            className="lg:mt-9 mt-6"
            classPaginator="paginator-sidebar"
            handleSubmit={(params) => {
              if (isMobile) {
                params.id && onHandleRequestRecived(params);
              } else {
                params.action && onHandleRequestRecived(params);
              }
            }}
            list={invitationDataList}
            paginator={isDesktop}
            title="Te invitaron a participar"
            variant={isMobile ? 'default' : 'two-actions'}
          />
        )
      );
    }, [invitationDataList, isDesktop, isMobile, onHandleRequestRecived]);

    // IR A VER MINILIGAS
    const onHandlePlaying = useCallback(
      ({ id }: ActionsTourneys) => {
        const dataML = playingList?.find((item: MiniLigasTO) => item.id === id);

        if (!dataML) return;

        if (
          dataML?.idEstadoML === 2 ||
          (dataML.isOwner && dataML?.idEstadoML === 2)
        ) {
          updateTourney({
            id,
            isOwner: true,
          });
          navigate(ROUTES.minileagues.view, {
            state: {
              isFinalDes: initData?.confDesenlace,
              comesFromFinal: location?.state?.comesFromFinal || false,
            },
          });
        } else {
          navigate(ROUTES.minileagues.admin, {
            state: { minileagueId: id, maxPlayers: initData?.cantPartMaxXML },
          });
        }
      },
      [
        initData?.cantPartMaxXML,
        initData?.confDesenlace,
        location?.state?.comesFromFinal,
        navigate,
        playingList,
        updateTourney,
      ]
    );

    // IR A VER previas MINILIGAS
    const onHandlePrevious = useCallback(
      ({ id }: ActionsTourneys) => {
        const dataMl =
          previousList && previousList.find((item) => item.id === id);

        if (dataMl) {
          // esta ruta tiene que cambiar a la de miniligas
          navigate(ROUTES.minileagues.reactivate, {
            state: {
              MLname: dataMl.nombreML,
              MLid: id,
            },
          });

          updateTourney({
            tourney: dataMl,
          });
        }
      },
      [navigate, previousList, updateTourney]
    );

    const Content = useCallback(() => {
      return (
        <>
          {/* TE INVITARON A PARTICIPAR */}
          {!tourneyData.tourneyStarted && <RequestRecivedListUI />}

          {/* EQUIPOS */}
          {playingDataList && playingDataList?.length > 0 && (
            <TourneyList
              action={{
                txtAction: 'Ver Miniliga', // posiblemente hacer condicional  para administrar o ver miniliga
                variantButton: 'secondary',
                txtActionDisabled: 'Ver Miniliga', // posiblemente hacer condicional  para administrar o ver miniliga
              }}
              className="lg:mt-9 mt-6"
              handleSubmit={onHandlePlaying}
              list={playingDataList}
              paginator={isDesktop}
              title="Estás participando"
            />
          )}

          {pendingDataList && pendingDataList.length > 0 && (
            <TourneyList
              className="lg:mt-9 mt-6"
              classNames={{
                titleContainer: 'px-4 md:px-0',
              }}
              list={pendingDataList}
              paginator={isDesktop}
              title="Esperando aceptación"
              variant="show-status"
            />
          )}

          {/* Miniligas edición pasada previousDataList */}
          {!tourneyData.tourneyStarted &&
            previousDataList &&
            previousDataList?.length > 0 && (
              <TourneyList
                action={{
                  txtAction: 'Reactivar',
                  variantButton: 'secondary',
                  txtActionDisabled: 'Reactivar',
                }}
                className="lg:mt-9 mt-6"
                disabled={isLastMatchDay}
                handleSubmit={onHandlePrevious}
                list={previousDataList}
                paginator={isDesktop}
                title="Miniligas edición pasada"
                // variant="show-status"
              />
            )}
          {((isDesktop &&
            invitationDataList &&
            invitationDataList?.length > 0) ||
            (isDesktop && playingDataList && playingDataList?.length > 0) ||
            (!tourneyData.tourneyStarted &&
              previousDataList &&
              previousDataList?.length > 0)) && (
            <MoreInfoButton className="mt-8 font-bold" />
          )}
        </>
      );
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      RequestRecivedListUI,
      isDesktop,
      isMobile,
      onHandlePlaying,
      playingDataList,
    ]);

    const getSubtitle = (initData: Maybe<ResponseInitMiniligas>) => {
      if (isParticipationWaitingForAutomaticMl || isMiniLeaguesInProgress)
        return;
      if (location.state?.comesFromFinal)
        return 'Estas son las Miniligas en las que participaste. Consultá si ganaste alguna y clasificaste a la final por uno de los 5 premios.';
      if (initData?.comenzaron)
        return `Ganá una Miniliga y convertite en su representante para la Gran Final de la ${initData?.nombreFechaDesenlace}.`;
      return `Creá o buscá una Miniliga antes de la ${
        initData?.nombrePrimeraFechaML
      } y competí contra ${
        (initData?.cantPartMaxXML ?? 0) - 1
      } rivales en desafíos mano a mano para ganar tu pase a la Gran Final.`;
    };

    // (externalizar)
    const onHandleLinkInvitation = async (idMiniliga: number) => {
      if (state.processingInvitation) {
        return;
      }

      setState((prevState) => ({
        ...prevState,
        processingInvitation: true,
      }));

      try {
        // Para invitaciones por link
        const responsePostulation = await requestPostulationMl(idMiniliga);

        if (responsePostulation.estado) {
          toast.show(
            'Enviaste tu solicitud para unirte a la Miniliga ' +
              state.invitationDetails?.nombre +
              '.'
          );
        } else {
          // Mostrar el mensaje específico del backend
          if (responsePostulation.mensaje) {
            toast.show(responsePostulation.mensaje);
          } else {
            toast.show(TEXTS.defaultError);
          }
        }
      } catch (error) {
        console.error(error);
        toast.show(TEXTS.defaultError);
      } finally {
        // Limpiar el estado y localStorage
        if (
          localStorage.getItem('invitationType') === InvitationType.MINILIGA
        ) {
          cleanStorageRedirectInvitation();
        }

        // Resetear completamente el estado
        setState(initialState);
        fetchedRef.current = false; // Resetear el ref
        // Refrescar los datos
        refetchFather();
      }
    };

    // (externalizar)
    const handleCloseInvitationModal = () => {
      // Limpiar localStorage
      if (localStorage.getItem('invitationType') === InvitationType.MINILIGA) {
        cleanStorageRedirectInvitation();
      }
      // Resetear estado
      setState((prevState) => ({
        ...prevState,
        showModalInvitationFromUrl: false,
        invitationDetails: null,
      }));

      fetchedRef.current = false;
    };

    const mainBoxHandler = useCallback(() => {
      if (isParticipationWaitingForAutomaticMl) {
        if (isDesktop) {
          return (
            <MainBox
              className="!mt-6"
              context={EnumContextGDT.MiniLeaguesInProgress}
            >
              <h1 className="font-bold text-xl">
                Se están jugando desde la {initData?.nombrePrimeraFechaML}.
              </h1>
              <p className="text-gdt-gray-2 pt-16 font-semibold">
                Conocé el martes los resultados de la primera fecha de
                competencia de la Miniliga en la que estás participando.
              </p>
              <MoreInfoButton className="mt-20 mx-auto font-bold" />
            </MainBox>
          );
        } else if (isMobile) {
          return (
            <div className="p-4 mt-6 text-center">
              <h1 className="font-bold text-xl">
                Se están jugando desde la {initData?.nombrePrimeraFechaML}.
              </h1>
              <p className="text-gdt-gray-2 py-14 font-semibold">
                Conocé el martes los resultados de la primera fecha de
                competencia de la Miniliga en la que estás participando.
              </p>
            </div>
          );
        }
      }
      if (isMiniLeaguesInProgress) {
        if (isDesktop) {
          return (
            <MainBox
              className="!mt-6"
              context={EnumContextGDT.MinileaguesDisabled}
            >
              <h1 className="font-bold text-xl">
                Lamentablemente, ya no podés participar.
              </h1>
              <p className="text-gdt-gray-2 pt-16 font-semibold">
                Las Miniligas ya comenzaron.
              </p>
              <MoreInfoButton className="mt-20 mx-auto font-bold" />
            </MainBox>
          );
        } else if (isMobile) {
          return (
            <div className="p-4 mt-6 text-center">
              <h1 className="font-bold text-xl">
                Lamentablemente, ya no podés participar.
              </h1>
              <p className="text-gdt-gray-2 py-14 font-semibold">
                Las Miniligas ya comenzaron.
              </p>
            </div>
          );
        }
      }

      if (
        isDesktop &&
        !invitationDataList?.length &&
        !playingDataList?.length
      ) {
        return (
          <MainBox className="!mt-6" context={EnumContextGDT.Minileagues}>
            <p className="text-gdt-gray-2">
              Todavía no estás participando en ninguna Miniliga.
              <br />
              <strong>
                Creá una nueva o <br />
                inscribite a una de las recomendadas.
              </strong>
            </p>
            <Button
              className="w-[187px] mt-8 mx-auto"
              disabled={tourneyData.tourneyStarted}
              type="button"
              variant="primary"
              onClick={handleButtonCreate}
            >
              Crear Miniliga
            </Button>
            <MoreInfoButton className="mt-8 mx-auto font-bold" />
          </MainBox>
        );
      }

      return null;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      isDesktop,
      isMobile,
      isParticipationWaitingForAutomaticMl,
      playingDataList,
      tourneyData.tourneyStarted,
      updateTourney,
      handleButtonCreate,
    ]);

    return (
      <MainHome
        className="w-full"
        context={EnumContextGDT.Minileagues}
        subtitle={getSubtitle(initData)}
        title="Miniligas"
      >
        {isMounted && (
          <>
            <Content />
            {mainBoxHandler()}
            {/* MODAL INFO */}
            <Modal
              className="w-[600px] text-center px-[38px]"
              controlledOnClose={() => updateTourney({ showModalInfo: false })}
              isControlledOpen={Boolean(tourneyData.showModalInfo) && isDesktop}
              render={() => <InfoContent />}
              size="sm"
              variant="controlled"
            />
            {/* MODAL ACEPTACION ACEPTACION/RECHAZO INVITACION RECIBIDAS (externalizar) */}
            <Modal
              controlledOnClose={() =>
                setState({ ...state, showModalRequest: false })
              }
              isControlledOpen={state.showModalRequest && isMobile}
              render={() => {
                return (
                  <div className="text-center mt-2">
                    <p className="text-base font-medium">
                      <strong>{state.nameUser}</strong> te invitó a
                      <br />
                      participar de su Miniliga
                    </p>
                    <div className="flex space-x-2 my-10">
                      <Button
                        className="flex-1"
                        type="button"
                        variant="secondary"
                        onClick={() =>
                          onHandleRequestActions(
                            ActionTypeManageFCupEnum.ACC_RECHAZAR_INVITACION,
                            state.idMiniLiga
                          )
                        }
                      >
                        Rechazar
                      </Button>
                      <Button
                        className="flex-1"
                        type="button"
                        variant="primary"
                        onClick={() =>
                          onHandleRequestActions(
                            ActionTypeManageFCupEnum.ACC_ACEPTAR_INVITACION,
                            state.idMiniLiga
                          )
                        }
                      >
                        Aceptar
                      </Button>
                    </div>
                  </div>
                );
              }}
              variant="controlled"
            />

            {/* MODAL PARA INVITACIONES DESDE URL */}
            <Modal
              controlledOnClose={handleCloseInvitationModal}
              isControlledOpen={Boolean(
                state.showModalInvitationFromUrl &&
                  !state.processingInvitation &&
                  state.invitationDetails
              )}
              render={() => {
                return (
                  <div className="text-center mt-2">
                    <p className="text-base text-[#666666]">
                      <strong>{state.nameUser}</strong> te invitó a
                      <br />
                      postularte a su Miniliga
                    </p>
                    <div className="flex space-x-2 my-6">
                      <Button
                        className="flex-1 !bg-[#EEF2FF] !text-[#3366FF] hover:!bg-[#E5E9FF]"
                        type="button"
                        variant="secondary"
                        onClick={handleCloseInvitationModal}
                      >
                        Rechazar
                      </Button>
                      <Button
                        className="flex-1 !bg-[#3366FF] hover:!bg-[#2952CC]"
                        type="button"
                        variant="primary"
                        onClick={
                          isAlreadyInvited
                            ? () =>
                                onHandleRequestActions(
                                  ActionTypeManageFCupEnum.ACC_ACEPTAR_INVITACION,
                                  state.idMiniLiga
                                )
                            : () =>
                                onHandleLinkInvitation(state.idMiniLiga ?? 0)
                        }
                      >
                        Aceptar
                      </Button>
                    </div>
                  </div>
                );
              }}
              variant="controlled"
            />

            {/* MODAL PARA INVITACIONES DESDE TKRED */}
            {dataPostLoginTkred && (
              <ModalTkred
                data={dataPostLoginTkred}
                handleRequestActions={onHandleRequestActions}
              />
            )}
          </>
        )}
      </MainHome>
    );
  }
);

MainContent.displayName = 'MainContent';
